import Button from "components/ui/Button";
import {
	ChevronLeft,
	ChevronRight,
	Clock,
	Mail,
	Phone,
	User,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { formatDateTime, formatDateOnly } from "../../../utils/formatDate";

interface ViewingItem {
	id: string;
	status: string;
	property_name?: string;
	buyer_name?: string;
	buyer_email?: string;
	buyer_phone?: string;
	viewing_date?: string;
	notes?: string;
	duration?: number;
	[key: string]: any;
}

interface ViewingFilters {
	status: string;
	search?: string;
	[key: string]: any;
}

interface ViewingCalendarProps {
	viewings: ViewingItem[];
	onViewingAction: (viewing: ViewingItem, action: string) => void;
	filters: ViewingFilters;
}

const ViewingCalendar = ({
	viewings = [],
	onViewingAction,
	filters,
}: ViewingCalendarProps) => {
	const [currentDate, setCurrentDate] = useState(new Date());
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const detailsSectionRef = useRef<HTMLDivElement>(null);

	// Filter viewings based on current filters
	const filteredViewings = viewings?.filter((viewing) => {
		if (filters?.status !== "all" && viewing?.status !== filters?.status) {
			return false;
		}
		if (filters?.search) {
			const searchTerm = filters?.search?.toLowerCase();
			return (
				viewing?.property_name?.toLowerCase()?.includes(searchTerm) ||
				viewing?.buyer_name?.toLowerCase()?.includes(searchTerm)
			);
		}
		return true;
	});

	// Generate calendar days
	const generateCalendarDays = () => {
		const year = currentDate?.getFullYear();
		const month = currentDate?.getMonth();
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);
		const firstDayOfWeek = firstDay?.getDay();
		const daysInMonth = lastDay?.getDate();

		const days = [];

		// Add empty cells for days before the first day of the month
		for (let i = 0; i < firstDayOfWeek; i++) {
			days?.push(null);
		}

		// Add days of the month
		for (let day = 1; day <= daysInMonth; day++) {
			days?.push(new Date(year, month, day));
		}

		return days;
	};

	const getViewingsForDate = (date: Date | null) => {
		if (!date) return [];
		// Format date as YYYY-MM-DD in LOCAL timezone (not UTC)
		// This avoids timezone conversion issues
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		const dateString = `${year}-${month}-${day}`;

		return filteredViewings?.filter((viewing) => {
			// Handle both date string and datetime string formats
			const viewingDateStr = viewing.date || viewing.viewing_date;
			if (!viewingDateStr) return false;

			// Extract just the date part (YYYY-MM-DD) from the viewing date string
			// WITHOUT converting to UTC - just use the date part as-is
			const viewingDate = viewingDateStr.split('T')[0];
			return viewingDate === dateString;
		});
	};

	const navigateMonth = (direction: number) => {
		setCurrentDate((prev) => {
			const newDate = new Date(prev);
			newDate?.setMonth(prev?.getMonth() + direction);
			return newDate;
		});
	};

	const getStatusColor = (status: string): string => {
		const colors: Record<string, string> = {
			scheduled: "bg-blue-100 text-blue-800 border-blue-200",
			confirmed: "bg-green-100 text-green-800 border-green-200",
			completed: "bg-purple-100 text-purple-800 border-purple-200",
			cancelled: "bg-red-100 text-red-800 border-red-200",
		};
		return colors[status] || "bg-gray-100 text-gray-800 border-gray-200";
	};

	const formatTime = (timeOrDateTime: string | undefined): string => {
		// Handle both time string (HH:MM) and datetime string (ISO)
		if (!timeOrDateTime) return "";
		try {
			// If it's an ISO datetime string, extract the time part
			if (timeOrDateTime.includes('T')) {
				const timePart = timeOrDateTime.split('T')[1]?.split('.')[0]?.split('Z')[0];
				if (timePart) {
					// Parse HH:MM:SS or HH:MM
					const [hours, minutes] = timePart.split(':').map(Number);
					const date = new Date(2000, 0, 1, hours, minutes);
					return date.toLocaleTimeString("en-US", {
						hour: "numeric",
						minute: "2-digit",
						hour12: true,
					});
				}
			}

			// Otherwise, try treating it as a time string (HH:MM)
			if (/^\d{2}:\d{2}/.test(timeOrDateTime)) {
				const [hours, minutes] = timeOrDateTime.split(':').map(Number);
				const date = new Date(2000, 0, 1, hours, minutes);
				return date.toLocaleTimeString("en-US", {
					hour: "numeric",
					minute: "2-digit",
					hour12: true,
				});
			}

			// Fallback to original behavior
			const date = new Date(timeOrDateTime);
			if (isNaN(date.getTime())) return "";
			return date.toLocaleTimeString("en-US", {
				hour: "numeric",
				minute: "2-digit",
				hour12: true,
			});
		} catch (e) {
			return "";
		}
	};

	// Scroll to details section when a date is selected
	useEffect(() => {
		if (selectedDate && detailsSectionRef.current) {
			// Small delay to ensure the DOM has updated
			setTimeout(() => {
				detailsSectionRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});
			}, 100);
		}
	}, [selectedDate]);

	const calendarDays = generateCalendarDays();
	const monthNames = [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December",
	];
	const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

	return (
		<div className="p-4 sm:p-6">
			<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mb-6">
				<h2 className="text-lg font-semibold text-foreground">Calendar</h2>
				<div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
					<Button
						variant="outline"
						size="sm"
						onClick={() => navigateMonth(-1)}
						className="flex-shrink-0"
					>
						<ChevronLeft className="w-4 h-4" />
					</Button>
					<span className="text-base sm:text-lg font-semibold text-foreground min-w-0 flex-1 sm:min-w-[180px] text-center truncate px-2">
						{monthNames?.[currentDate?.getMonth()]} {currentDate?.getFullYear()}
					</span>
					<Button
						variant="outline"
						size="sm"
						onClick={() => navigateMonth(1)}
						className="flex-shrink-0"
					>
						<ChevronRight className="w-4 h-4" />
					</Button>
				</div>
			</div>
			<div className="grid grid-cols-7 gap-1 mb-4">
				{dayNames?.map((day) => (
					<div
						key={day}
						className="p-2 sm:p-3 text-center text-xs sm:text-sm font-medium text-muted-foreground"
					>
						{day}
					</div>
				))}
			</div>
			<div className="grid grid-cols-7 gap-1">
				{calendarDays?.map((day, index) => {
					const dayViewings = day ? getViewingsForDate(day) : [];
					const isToday =
						day && day?.toDateString() === new Date()?.toDateString();
					const isSelected =
						selectedDate &&
						day &&
						day?.toDateString() === selectedDate?.toDateString();

					return (
						<div
							key={index}
							className={`min-h-[80px] sm:min-h-[120px] border border-border rounded-lg p-1 sm:p-2 ${
								day ? "bg-card hover:bg-muted/50 cursor-pointer" : "bg-muted/20"
							} ${isToday ? "ring-2 ring-primary" : ""} ${
								isSelected ? "bg-muted" : ""
							}`}
							onClick={() => day && setSelectedDate(day)}
						>
							{day && (
								<>
									<div
										className={`text-xs sm:text-sm font-medium mb-1 sm:mb-2 ${
											isToday ? "text-primary" : "text-foreground"
										}`}
									>
										{day?.getDate()}
									</div>

									<div className="space-y-0.5 sm:space-y-1">
										{dayViewings?.slice(0, 2)?.map((viewing) => (
											<div
												key={viewing?.id}
												className={`text-[10px] sm:text-xs p-0.5 sm:p-1 rounded border ${getStatusColor(
													viewing?.status,
												)}`}
											>
												<div className="font-medium truncate">
													{viewing?.property_name || "Unknown"}
												</div>
												<div className="flex items-center gap-0.5 sm:gap-1">
													<Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
													<span className="truncate">
														{formatTime(viewing?.viewing_date)}
													</span>
												</div>
											</div>
										))}

										{dayViewings?.length > 2 && (
											<div className="text-[10px] sm:text-xs text-muted-foreground p-0.5 sm:p-1">
												+{dayViewings?.length - 2} more
											</div>
										)}
									</div>
								</>
							)}
						</div>
					);
				})}
			</div>
			{/* Selected Date Details */}
			{selectedDate && (
				<div
					ref={detailsSectionRef}
					className="mt-4 sm:mt-6 border-t border-border pt-4 sm:pt-6"
				>
					<div className="mb-4 sm:mb-6">
						<h3 className="text-base sm:text-lg font-semibold text-foreground mb-1 sm:mb-2">
							Viewings for
						</h3>
						<p className="text-sm sm:text-base font-medium text-foreground">
							{selectedDate ? formatDateOnly(selectedDate.toISOString()) : ""}
						</p>
					</div>

					{getViewingsForDate(selectedDate)?.length === 0 ? (
						<p className="text-muted-foreground">
							No viewings scheduled for this date.
						</p>
					) : (
						<div className="space-y-3 sm:space-y-4">
							{getViewingsForDate(selectedDate)?.map((viewing) => (
								<div
									key={viewing?.id}
									className="bg-muted rounded-lg p-3 sm:p-4 overflow-hidden"
								>
									<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-0">
										<div className="flex-1 min-w-0">
											<div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3 sm:mb-2">
												<h4 className="text-sm sm:text-base font-medium text-foreground truncate">
													{viewing?.property_name || "Unknown Property"}
												</h4>
												<span
													className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 w-fit ${getStatusColor(
														viewing?.status,
													)}`}
												>
													{viewing?.status}
												</span>
											</div>

											<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-sm">
												<div className="flex items-center gap-2 min-w-0">
													<Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
													<span className="text-foreground truncate">
														{formatTime(viewing?.viewing_date)}
													</span>
												</div>
												<div className="flex items-center gap-2 min-w-0">
													<User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
													<span className="text-foreground truncate">
														{viewing?.buyer_name || "N/A"}
													</span>
												</div>
												<div className="flex items-center gap-2 min-w-0">
													<Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
													<span className="text-foreground truncate">
														{viewing?.buyer_email || "N/A"}
													</span>
												</div>
												<div className="flex items-center gap-2 min-w-0">
													<Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
													<span className="text-foreground truncate">
														{viewing?.buyer_phone || "N/A"}
													</span>
												</div>
											</div>

											{viewing?.notes && (
												<div className="mt-2 overflow-hidden">
													<p className="text-sm text-muted-foreground break-words">
														{viewing?.notes}
													</p>
												</div>
											)}
										</div>

										<div className="flex flex-wrap items-center gap-2 sm:ml-4 flex-shrink-0 w-full sm:w-auto">
											{viewing?.status === "scheduled" && (
												<Button
													size="sm"
													onClick={() => onViewingAction(viewing, "confirm")}
													className="flex-1 sm:flex-initial min-w-0"
												>
													<span className="truncate">Confirm</span>
												</Button>
											)}
											<Button
												variant="outline"
												size="sm"
												onClick={() => onViewingAction(viewing, "reschedule")}
												className="flex-1 sm:flex-initial min-w-0"
											>
												<span className="truncate">Reschedule</span>
											</Button>
											<Button
												variant="outline"
												size="sm"
												onClick={() => onViewingAction(viewing, "chat")}
												className="flex-1 sm:flex-initial min-w-0"
											>
												<span className="truncate">Chat</span>
											</Button>
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default ViewingCalendar;
